import React, { useEffect, useRef, useState } from 'react';
import '../../styles/interview-prelude.css';

/*
 * InterviewPrelude — the interview room's pre-live (and post-live) stage,
 * starring "Sparky": the app's ✦ spark as a tiny mascot. Pure CSS/SVG, themed
 * by the --mx accent. Every phase maps to a REAL state — never a fake timer:
 *
 *   preparing   session + 10-question pack building (NanoApp, voiceSession null)
 *   connecting  room mounted, token/mic/WebRTC in flight (status initializing)
 *   ready       connection up — Begin ignites (status connected, not started)
 *   joining     Begin pressed, waiting for Alex's first words
 *   grading     interview over, the evaluator is scoring the answers
 */

const CAPTIONS = {
  preparing: ['Reading your memory…', 'Picking your 10 questions…', 'Setting up the room…'],
  connecting: ['Connecting the room…'],
  ready: ['Your 10 questions are ready — Alex is waiting.'],
  joining: ['Alex is joining…'],
  grading: ['Reading your transcript…', 'Scoring each answer…', 'Writing your feedback…'],
};

const SUB = {
  preparing: 'The AI Selector is building this round from your weak spots and revisions.',
  connecting: 'Your browser may ask for the microphone — that’s the room opening.',
  ready: 'Press Begin and answer out loud, like a real interview.',
  joining: 'Say hello when you hear the voice.',
  grading: 'Hold tight — usually under a minute. Your answers are safe.',
};

/* The mascot family — one blinking-eyed character per round, same stagecraft. */

const SparkScene = ({ phase }) => (
  <div className="ip-scene" data-phase={phase}>
    <span className="ip-ring ip-r1" aria-hidden="true" />
    <span className="ip-ring ip-r2" aria-hidden="true" />
    <span className="ip-dot ip-d1" aria-hidden="true" />
    <span className="ip-dot ip-d2" aria-hidden="true" />
    <span className="ip-dot ip-d3" aria-hidden="true" />
    <svg className="ip-sparky" viewBox="0 0 120 120" role="img" aria-label="Interview assistant">
      <path className="ip-body"
        d="M60 8 C66 38 82 54 112 60 C82 66 66 82 60 112 C54 82 38 66 8 60 C38 54 54 38 60 8 Z" />
      <g className="ip-eyes">
        <circle cx="51" cy="57" r="4" />
        <circle cx="69" cy="57" r="4" />
      </g>
      <path className="ip-mouth" d="M53 70 Q60 76 67 70" fill="none" />
    </svg>
  </div>
);

/* Blitz: a bolt charging up inside a winding stopwatch ring. */
const BoltScene = ({ phase }) => (
  <div className="ip-scene ip-scene-blitz" data-phase={phase}>
    <svg className="ip-clockring" viewBox="0 0 170 170" aria-hidden="true">
      <circle cx="85" cy="85" r="76" fill="none" strokeDasharray="3 9" />
      <circle className="ip-clockhand" cx="85" cy="85" r="76" fill="none"
        strokeDasharray="60 418" strokeLinecap="round" />
    </svg>
    <svg className="ip-bolt" viewBox="0 0 120 120" role="img" aria-label="Blitz round">
      <path className="ip-body ip-bolt-body" d="M67 10 L34 66 L56 66 L50 110 L88 50 L64 50 Z" />
      <g className="ip-eyes">
        <circle cx="56" cy="46" r="3.6" />
        <circle cx="68" cy="42" r="3.6" />
      </g>
    </svg>
  </div>
);

/* Rapidfire: a mic warming up — waves out, equalizer bars finding the beat. */
const MicScene = ({ phase }) => (
  <div className="ip-scene ip-scene-rapidfire" data-phase={phase}>
    <span className="ip-ring ip-r1" aria-hidden="true" />
    <span className="ip-ring ip-r2" aria-hidden="true" />
    <svg className="ip-mic" viewBox="0 0 120 120" role="img" aria-label="Rapid fire round">
      <rect className="ip-body" x="42" y="14" width="36" height="58" rx="18" />
      <path className="ip-mic-arc" d="M30 56 a30 30 0 0 0 60 0" fill="none" />
      <line className="ip-mic-arc" x1="60" y1="88" x2="60" y2="104" />
      <line className="ip-mic-arc" x1="44" y1="104" x2="76" y2="104" />
      <g className="ip-eyes">
        <circle cx="52" cy="40" r="3.6" />
        <circle cx="68" cy="40" r="3.6" />
      </g>
      <path className="ip-mouth ip-mic-mouth" d="M54 52 Q60 57 66 52" fill="none" />
    </svg>
    <span className="ip-eq" aria-hidden="true"><i /><i /><i /><i /><i /></span>
  </div>
);

const SCENES = { interview: SparkScene, blitz: BoltScene, rapidfire: MicScene };

const InterviewPrelude = ({ phase = 'preparing', variant = 'interview', onBegin, onBack,
  captions: captionsOverride, sub: subOverride, begin = true }) => {
  const Scene = SCENES[variant] || SparkScene;
  const captions = captionsOverride || CAPTIONS[phase] || CAPTIONS.preparing;
  const [ci, setCi] = useState(0);
  useEffect(() => {
    setCi(0);
    if (captions.length < 2) return undefined;
    const t = setInterval(() => setCi((i) => (i + 1) % captions.length), 2400);
    return () => clearInterval(t);
  }, [phase, captions.length]);

  // grading shows an honest elapsed clock — evaluation takes real seconds
  const t0 = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (phase !== 'grading') return undefined;
    t0.current = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - t0.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const showBegin = begin && (phase === 'preparing' || phase === 'connecting' || phase === 'ready');
  return (
    <div className="ip-root" data-phase={phase} role="status" aria-live="polite">
      <Scene phase={phase} />
      <div className="ip-shimmer" aria-hidden="true" />
      <div className="ip-caption" key={captions[ci]}>{captions[ci]}</div>
      <div className="ip-sub">
        {subOverride !== undefined ? subOverride : SUB[phase]}
        {phase === 'grading' && elapsed > 0 && (
          <span className="ip-clock"> · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</span>
        )}
      </div>
      {showBegin && (
        <button className="ip-begin" type="button" disabled={phase !== 'ready'} onClick={onBegin}>
          ▶ Begin Interview
        </button>
      )}
      {onBack && phase !== 'grading' && phase !== 'joining' && (
        <button className="ip-back" type="button" onClick={onBack}>← Back</button>
      )}
    </div>
  );
};

export default InterviewPrelude;
